import React, { useState, useEffect, useCallback } from 'react';
import { ArrowPathIcon, TruckIcon, ExclamationTriangleIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { fetchShipments, fetchB2bOrders } from '../api/marketplaceApi';
import StatusBadge from '../components/StatusBadge';

const BuyerDeliveries: React.FC = () => {
  const [shipments, setShipments] = useState<any[]>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [shipmentRes, orderRes] = await Promise.all([
        fetchShipments().catch(() => ({ items: [] })),
        fetchB2bOrders({ status: 'confirmed' }).catch(() => ({ items: [] })),
      ]);

      setShipments(shipmentRes.items || shipmentRes.data || []);
      setOrders(orderRes.items || orderRes.data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeDeliveries = shipments.filter(s =>
    ['created', 'picked_up', 'in_transit', 'partially_delivered'].includes(s.status)
  );

  const completedDeliveries = shipments.filter(s =>
    ['delivered', 'confirmed'].includes(s.status)
  );

  const pendingDeliveryOrders = orders.filter(o =>
    ['confirmed', 'processing'].includes(o.status) && !shipments.some(s => s.order_id === o.id)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="relative">
          <div className="w-10 h-10 border-4 border-emerald-200 rounded-full"></div>
          <div className="w-10 h-10 border-4 border-emerald-600 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-400 mx-auto" />
        <p className="text-red-700 font-medium mt-2">{error}</p>
        <button onClick={fetchData} className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Deliveries</h2>
        <button onClick={fetchData} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Active Shipments */}
      <div>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">In Transit ({activeDeliveries.length})</h3>
        {activeDeliveries.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-xl p-8 text-center">
            <TruckIcon className="w-10 h-10 text-gray-300 mx-auto" />
            <p className="text-gray-500 mt-2 text-sm">No active shipments</p>
          </div>
        ) : (
          <div className="space-y-3">
            {activeDeliveries.map((shipment: any) => (
              <div key={shipment.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <TruckIcon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-gray-900">{shipment.shipment_number || `SHP-${shipment.id}`}</p>
                      <p className="text-xs text-gray-500">{shipment.carrier_name || 'Carrier TBD'}</p>
                    </div>
                  </div>
                  <StatusBadge status={shipment.status} size="sm" />
                </div>
                {shipment.tracking_url && (
                  <a href={shipment.tracking_url} target="_blank" rel="noopener noreferrer"
                     className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                    <MapPinIcon className="w-3 h-3" />
                    Track shipment
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pending Delivery Orders */}
      {pendingDeliveryOrders.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Awaiting Shipment ({pendingDeliveryOrders.length})</h3>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600">Order</th>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600">Total</th>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600">Delivery Address</th>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {pendingDeliveryOrders.map((o: any) => (
                  <tr key={o.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-mono text-xs font-medium">{o.order_number}</td>
                    <td className="px-4 py-2.5 font-bold text-sm">
                      {o.currency || 'RWF'} {(o.total_amount || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs max-w-[200px] truncate">{o.delivery_address || '—'}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={o.status} size="sm" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Completed */}
      {completedDeliveries.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Completed ({completedDeliveries.length})</h3>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600">Shipment</th>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600">Carrier</th>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600">Status</th>
                  <th className="px-4 py-2.5 text-left font-medium text-gray-600">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {completedDeliveries.map((s: any) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-mono text-xs">{s.shipment_number || `SHP-${s.id}`}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{s.carrier_name || '—'}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={s.status} size="sm" /></td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">
                      {s.delivered_at ? new Date(s.delivered_at).toLocaleDateString() :
                       s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {shipments.length === 0 && orders.length === 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-12 text-center">
          <TruckIcon className="w-12 h-12 text-gray-300 mx-auto" />
          <p className="text-gray-500 mt-3 font-medium">No deliveries yet</p>
          <p className="text-gray-400 text-sm mt-1">Shipments and delivery tracking will appear here.</p>
        </div>
      )}
    </div>
  );
};

export default BuyerDeliveries;
